"use client"

import * as React from "react"
import { LucideIcon } from 'lucide-react'
import { Heart, Star } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardEcommerceProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  title: string
  description?: string
  imageUrl?: string
  imageAlt?: string
  price: number
  originalPrice?: number
  currency?: string
  rating?: number
  reviewCount?: number
  badge?: string
  badgeVariant?: 'default' | 'accent' | 'secondary' | 'success' | 'destructive'
  inStock?: boolean
  stockStatus?: string
  primaryAction?: {
    text: string
    onClick: () => void
    variant?: 'default' | 'accent' | 'outline'
  }
  secondaryAction?: {
    text: string
    onClick: () => void
    icon?: LucideIcon
  }
  children?: React.ReactNode
}

/**
 * E-commerce Product Card Component
 * Perfect for showcasing products with pricing, ratings, and purchase actions
 * Optimized for online stores and product catalogs
 */
export const UICardEcommerce = React.forwardRef<HTMLDivElement, UICardEcommerceProps>(
  ({
    title,
    description,
    imageUrl,
    imageAlt,
    price,
    originalPrice,
    currency = "$",
    rating,
    reviewCount,
    badge,
    badgeVariant = 'accent',
    inStock = true,
    stockStatus,
    primaryAction,
    secondaryAction,
    className,
    children,
    ...props
  }, ref) => {
    const [isWishlisted, setIsWishlisted] = React.useState(false)

    const handleWishlistToggle = () => {
      setIsWishlisted(!isWishlisted)
      secondaryAction?.onClick()
    }

    const formatPrice = (amount: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency === '$' ? 'USD' : 'KRW',
        minimumFractionDigits: currency === '$' ? 2 : 0,
      }).format(amount)
    }

    const renderStars = (rating: number) => {
      return Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          className={cn(
            "h-4 w-4",
            index < rating
              ? "fill-yellow-400 text-yellow-400"
              : "fill-muted text-muted-foreground"
          )}
        />
      ))
    }

    return (
      <UICard
        ref={ref}
        className={cn(
          "group relative overflow-hidden",
          "hover:shadow-xl dark:hover:shadow-primary/10",
          "transition-shadow duration-300",
          "max-w-sm",
          className
        )}
        {...props}
      >
        {/* Badge */}
        {badge && (
          <UIBox className="absolute top-3 right-3 z-10">
<UITypography
              as="span"
              variant="caption"
              size="xs"
              weight="medium"
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5",
                {
                  "bg-primary/10 text-primary dark:bg-primary/20": badgeVariant === 'default',
                  "bg-accent/80 text-accent-foreground dark:bg-accent/70 dark:text-accent-foreground": badgeVariant === 'accent',
                  "bg-secondary/10 text-secondary-foreground": badgeVariant === 'secondary',
                  "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200": badgeVariant === 'success',
                  "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200": badgeVariant === 'destructive',
                }
              )}
            >
              {badge}
            </UITypography>
          </UIBox>
        )}

        {/* Product Image */}
        <UICardHeader className="p-0">
          <UIBox className="relative aspect-square overflow-hidden bg-muted">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={imageAlt || title}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = 'none'
                  target.parentElement!.classList.add('flex', 'items-center', 'justify-center')
                  const fallbackDiv = document.createElement('div')
                  fallbackDiv.className = 'text-center'
                  const fallbackContent = document.createElement('div')
                  fallbackContent.innerHTML = `
                    <UIBox class="text-4xl mb-2">📦</UIBox>
                  `
                  const titleElement = document.createElement('div')
                  titleElement.className = 'text-sm text-muted-foreground'
                  titleElement.textContent = title
                  fallbackDiv.appendChild(fallbackContent)
                  fallbackDiv.appendChild(titleElement)
                  target.parentElement!.appendChild(fallbackDiv)
                }}
              />
            ) : (
<UIBox className="flex h-full items-center justify-center">
                <UIBox className="text-center">
                  <UITypography as="div" variant="p" size="4xl" className="mb-2">📦</UITypography>
                  <UITypography variant="p" size="sm" color="muted">{title}</UITypography>
                </UIBox>
              </UIBox>
            )}
          </UIBox>
        </UICardHeader>

        {/* Product Info */}
        <UICardContent className="p-4">
          <UIBox className="space-y-3">
            {/* Title */}
<UITypography
              as="h3"
              variant="h3"
              size="default"
              weight="semibold"
              className="leading-tight group-hover:text-accent transition-colors"
            >
              {title}
            </UITypography>

            {/* Description */}
            {description && (
<UITypography variant="p" size="sm" color="muted" className="leading-relaxed">
                {description}
              </UITypography>
            )}

            {/* Rating */}
            {rating && (
              <UIBox className="flex items-center gap-2">
                <UIBox className="flex items-center gap-1">
                  {renderStars(rating)}
                </UIBox>
<UITypography as="span" variant="p" size="sm" weight="medium">{rating}</UITypography>
                {reviewCount && (
<UITypography as="span" variant="p" size="sm" color="muted">
                    ({reviewCount.toLocaleString()} reviews)
                  </UITypography>
                )}
              </UIBox>
            )}

            {/* Price */}
            <UIBox className="flex items-center gap-2">
<UITypography as="span" variant="h2" size="xl" weight="bold" color="default">
                {formatPrice(price)}
              </UITypography>
              {originalPrice && originalPrice > price && (
<UITypography as="span" variant="p" size="sm" color="muted" className="line-through">
                  {formatPrice(originalPrice)}
                </UITypography>
              )}
            </UIBox>

            {/* Stock Status */}
            {stockStatus && (
              <UIBox className="flex items-center gap-1">
                <div className={cn(
                  "h-2 w-2 rounded-full",
                  inStock ? "bg-green-500" : "bg-red-500"
                )} />
<UITypography
                  as="span"
                  variant="p"
                  size="sm"
                  weight="medium"
                  className={cn(
                    inStock ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                  )}
                >
                  {stockStatus}
                </UITypography>
              </UIBox>
            )}

            {children}
          </UIBox>
        </UICardContent>

        {/* Actions */}
        <UICardFooter className="p-4 pt-0">
          <UIBox className="flex w-full gap-2">
            {primaryAction && (
              <UIButton
                variant={primaryAction.variant || 'default'}
                onClick={primaryAction.onClick}
                className="flex-1"
                disabled={!inStock}
              >
                {primaryAction.text}
              </UIButton>
            )}

            {secondaryAction && (
              <UIButton
                variant="outline"
                size="icon"
                onClick={handleWishlistToggle}
                className={cn(
                  "shrink-0",
                  isWishlisted && "text-red-500 border-red-500"
                )}
              >
                {secondaryAction.icon ? (
                  <secondaryAction.icon className="h-4 w-4" />
                ) : (
                  <Heart className={cn("h-4 w-4", isWishlisted && "fill-current")} />
                )}
              </UIButton>
            )}
          </UIBox>
        </UICardFooter>
      </UICard>
    )
  }
)

UICardEcommerce.displayName = "UICardEcommerce"